import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./Login.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }

    // Check if user is already logged in
    const adminData = sessionStorage.getItem("adminUsers");
    if (adminData) {
      navigate("/admin");
    }
  }, [navigate]);

  // Clear error when user starts typing
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Client-side validation
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      setLoading(false);
      return;
    }

    try {
      // Check if the user exists in the admin collection
      const adminCollection = collection(db, "adminUsers");
      const q = query(
        adminCollection,
        where("email", "==", email.toLowerCase().trim()),
        where("role", "in", [0, 1])
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setError("Access denied. Only authorized admin users can log in.");
        setLoading(false);
        return;
      }

      // Get the admin document
      const adminDoc = querySnapshot.docs[0];
      const adminData = adminDoc.data();

      // Check if the password matches
      if (adminData.password !== password) {
        setError("Invalid email or password.");
        setLoading(false);
        return;
      }

      // Sign in with Firebase Auth
      await signInWithEmailAndPassword(auth, email.toLowerCase().trim(), password);
      
      // Store admin info in sessionStorage
      sessionStorage.setItem("adminUsers", JSON.stringify({
        email: adminData.email,
        role: adminData.role,
        uid: adminDoc.id,
        loginTime: new Date().toISOString()
      }));

      // Handle remember me functionality
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email.toLowerCase().trim());
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      // Success feedback
      navigate("/admin");
    } catch (err) {
      console.error("Login error:", err);
      
      // Enhanced error handling
      const errorMessages = {
        "auth/user-not-found": "No user found with this email address.",
        "auth/wrong-password": "Incorrect password.",
        "auth/invalid-email": "Invalid email address format.",
        "auth/too-many-requests": "Too many failed attempts. Please try again later.",
        "auth/network-request-failed": "Network error. Please check your connection.",
        "auth/user-disabled": "This account has been disabled.",
        "auth/invalid-credential": "Invalid credentials. Please check your email and password."
      };

      setError(errorMessages[err.code] || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError("");
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="logo-container">
          <img
            src="/logo.png"
            alt="YBe Logo"
            className="login-logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        
        <h2 className="login-title">YBe Portal</h2>
        <p className="login-subtitle">Sign in to access your account</p>
        
        <form onSubmit={handleLogin} className="login-form" noValidate>
          <div className="input-group">
            <label htmlFor="email" className="label">
              Email Address
            </label>
            <div className="input-wrapper">
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                required
                placeholder="admin@example.com"
                className="input-field"
                disabled={loading}
                autoComplete="email"
                aria-label="Email address"
                aria-invalid={error ? "true" : "false"}
              />
              <span className="input-icon">📧</span>
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="password" className="label">
              Password
            </label>
            <div className="input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={handlePasswordChange}
                required
                placeholder="Enter your password"
                className="input-field password-field"
                disabled={loading}
                autoComplete="current-password"
                aria-label="Password"
                aria-invalid={error ? "true" : "false"}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>

          {/* <div className="remember-me-container">
            <label className="remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
                className="remember-me-checkbox"
              />
              <span>Remember my email</span>
            </label>
          </div> */}

          {error && (
            <div className="error-message" role="alert" aria-live="polite">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="responsive-login-button"
            disabled={loading || !email || !password}
          >
            {loading ? (
              <span className="loading-spinner">
                <span className="spinner"></span>
                Logging in...
              </span>
            ) : (
              "Login"
            )}
          </button>
        </form>
        
        <div className="divider">
          <span>Need Help?</span>
        </div>

        <div className="link-container">
          <div className="link-row">
            <a href="/new-privacy-policy" className="link">
              <span className="link-icon">🔒</span>
              Privacy Policy
            </a>
            <a href="/delete-account" className="link">
              <span className="link-icon">🗑️</span>
              Delete Account
            </a>
          </div>
          <a 
            href="https://play.google.com/store/apps/details?id=com.ybe.social" 
            className="link playstore-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="link-icon">
              <img src="/play_store.webp" alt="Play Store" style={{ width: '20px', height: '20px' }} />
            </span>
            Download from Play Store
          </a>
        </div>

        <div className="footer-text">
          <p>© 2024 YBe. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;